/**
 * Analytics wrapper for Google Analytics and Mixpanel
 * Provides a unified interface for tracking events across the application
 */

// Google Analytics
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

// Mixpanel
let mixpanel: any = null;

// Initialize Mixpanel if available
if (typeof window !== "undefined") {
  try {
    // Mixpanel will be loaded via script tag
    mixpanel = (window as any).mixpanel;
  } catch (error) {
    console.warn("Mixpanel not available:", error);
  }
}

/**
 * Track a page view
 */
export function trackPageView(path: string, title?: string): void {
  // Google Analytics
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("config", process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "", {
      page_path: path,
      page_title: title,
    });
  }

  // Mixpanel
  if (mixpanel && mixpanel.track) {
    mixpanel.track("Page View", {
      path,
      title,
    });
  }
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  // Google Analytics
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, properties);
  }

  // Mixpanel
  if (mixpanel && mixpanel.track) {
    mixpanel.track(eventName, properties);
  }
}

/**
 * Track sentiment analysis events
 */
export function trackSentimentAnalysis(keyword: string, success: boolean): void {
  trackEvent("sentiment_analysis", {
    keyword,
    success,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track subscription events
 */
export function trackSubscriptionEvent(
  eventType: "signup" | "upgrade" | "downgrade" | "cancel",
  plan?: string
): void {
  trackEvent("subscription_event", {
    event_type: eventType,
    plan,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track button clicks
 */
export function trackButtonClick(buttonName: string, location?: string): void {
  trackEvent("button_click", {
    button_name: buttonName,
    location,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track form submissions
 */
export function trackFormSubmit(formName: string, success: boolean): void {
  trackEvent("form_submit", {
    form_name: formName,
    success,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track feature usage
 */
export function trackFeatureUsage(
  featureName: string,
  properties?: Record<string, any>
): void {
  trackEvent("feature_usage", {
    feature_name: featureName,
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Set user properties (for Mixpanel)
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (mixpanel && mixpanel.people && mixpanel.people.set) {
    mixpanel.people.set(properties);
  }
}

/**
 * Identify user (for Mixpanel)
 */
export function identifyUser(userId: string, properties?: Record<string, any>): void {
  if (mixpanel && mixpanel.identify) {
    mixpanel.identify(userId);
    if (properties) {
      setUserProperties(properties);
    }
  }
}

